// src/pages/Orders.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  message,
  Spin,
  Image,
  Input,
  Select,
  Modal,
  Tooltip,
  Form,
  Input as AntInput,
  Empty,
  Tag,
  Checkbox,
} from "antd";
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  DeleteOutlined,
  StopOutlined,
  SearchOutlined,
  FilterOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined as ExclamationCircleOutlinedIcon,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useOrderContext } from "../contexts/OrderContext";
import axiosInstance from "../utils/axiosInstance";

const { Option } = Select;
const { confirm } = Modal;
const { TextArea } = AntInput;

const splitProductName = (text) => {
  const words = (text || "").split(" ");
  if (words.length <= 2) return text;
  const mid = Math.ceil(words.length / 2);
  return (
    <>
      {words.slice(0, mid).join(" ")}
      <br />
      {words.slice(mid).join(" ")}
    </>
  );
};

const getCompanyName = (rec) =>
  rec?.company_name ||
  rec?.customer_company ||
  rec?.customerCompany ||
  rec?.user_company ||
  rec?.user?.company_name ||
  rec?.user?.companyName ||
  rec?.customer?.company_name ||
  rec?.customer?.companyName ||
  "-";

const safeJsonParse = (v) => {
  if (v == null) return null;
  if (typeof v === "object") return v;
  if (typeof v !== "string") return null;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const isCustomerOrderRecord = (rec) => {
  if (!rec) return false;
  if (rec.isCustomOrder === true) return true;

  const sqRaw = rec.special_quote ?? rec.specialQuote ?? null;
  const sq = typeof sqRaw === "object" ? sqRaw : safeJsonParse(sqRaw);
  if (sq?.customOrder?.enabled === true) return true;

  if (String(rec.orderId ?? "").startsWith("CO-")) return true;

  return false;
};

const resolveUrl = (u) => {
  const url = String(u || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  const base = (axiosInstance?.defaults?.baseURL || "").trim();
  if (!base) return url;
  const b = base.replace(/\/$/, "");
  const p = url.startsWith("/") ? url : `/${url}`;
  return `${b}${p}`;
};

const fileNameFromUrl = (url) => {
  try {
    const clean = String(url || "").split("?")[0].split("#")[0];
    const parts = clean.split("/");
    return decodeURIComponent(parts[parts.length - 1] || "file");
  } catch {
    return "file";
  }
};

const isPdfUrl = (url) => /\.pdf(\?|#|$)/i.test(String(url || ""));
const isImageUrl = (url) =>
  /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(String(url || ""));

const normalizeSpecialQuote = (order) => {
  const raw =
    order?.special_quote ||
    order?.specialQuote ||
    order?.specialInstruction ||
    order?.specialInstructions ||
    order?.special_quote_json ||
    null;

  const obj = safeJsonParse(raw);

  const text =
    (obj && (obj.text || obj.note || obj.message)) ||
    (typeof raw === "string" && !obj ? raw : "") ||
    "";

  const filesRaw =
    (obj && (obj.files || obj.attachments || obj.uploads)) ||
    order?.files ||
    order?.attachments ||
    [];

  const filesArr = Array.isArray(filesRaw) ? filesRaw : [];

  const files = filesArr
    .map((f) => {
      if (!f) return null;
      if (typeof f === "string") {
        const url = resolveUrl(f);
        const name = fileNameFromUrl(url);
        return { url, name, isPdf: isPdfUrl(url), isImage: isImageUrl(url) };
      }
      const url =
        resolveUrl(
          f.url || f.fileUrl || f.path || f.file || f.location || f.link || f.src || ""
        ) || "";
      if (!url) return null;
      const name =
        f.name ||
        f.fileName ||
        f.filename ||
        f.originalName ||
        f.original_name ||
        fileNameFromUrl(url);
      const mime = String(f.mimeType || f.mimetype || f.type || "").toLowerCase();
      const isPdf = mime.includes("pdf") || isPdfUrl(url);
      const isImage = mime.startsWith("image/") || isImageUrl(url);
      return { url, name, isPdf, isImage };
    })
    .filter(Boolean);

  const hasAny = Boolean(String(text || "").trim()) || files.length > 0;
  return { text: String(text || ""), files, hasAny };
};

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (value) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  date.setHours(0, 0, 0, 0);
  return date;
};

const addDays = (date, days) => {
  if (!date) return null;

  const next = new Date(date);
  next.setDate(next.getDate() + Number(days || 0));
  next.setHours(0, 0, 0, 0);
  return next;
};

const formatShortDate = (value) => {
  const date = startOfDay(value);
  if (!date) return "-";

  return date.toLocaleDateString(undefined, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
};

const getOrderBaseDate = (order) =>
  order?.orderDate ||
  order?.order_date ||
  order?.createdAt ||
  order?.created_at ||
  order?.created;

const getExplicitDeliveredByDate = (order) =>
  order?.delivered_by ||
  order?.deliver_by ||
  order?.deliverBy ||
  order?.deliveryBy ||
  order?.expected_date ||
  order?.expectedDate ||
  order?.deliveryDeadline ||
  order?.delivery_deadline ||
  order?.special_quote?.deliver_by ||
  order?.special_quote?.deliverBy ||
  null;

const getDeliveredByDate = (order) => {
  const explicit = startOfDay(getExplicitDeliveredByDate(order));
  if (explicit) return explicit;

  const baseDate = startOfDay(getOrderBaseDate(order));
  if (!baseDate) return null;

  return addDays(baseDate, 14);
};

const isOrderDelivered = (order) => {
  const raw = `${order?.deliveryStatus || ""} ${order?.status || ""}`.toLowerCase();
  return raw.includes("delivered") || raw.includes("complete");
};

const getDeliveryDeadlineInfo = (order) => {
  const baseDate = startOfDay(getOrderBaseDate(order));
  const deliveredByDate = getDeliveredByDate(order);

  if (!baseDate || !deliveredByDate) {
    return {
      state: "unknown",
      label: "No date",
      color: "default",
      bgClass: "bg-gray-50 text-gray-500 border-gray-200",
      rowClassName: "",
      date: null,
      tooltip: "Order date is missing",
      daysFromOrder: 0,
      daysRemaining: 0,
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const daysFromOrder = Math.floor((today.getTime() - baseDate.getTime()) / DAY_MS);
  const daysRemaining = Math.ceil((deliveredByDate.getTime() - today.getTime()) / DAY_MS);

  if (isOrderDelivered(order)) {
    return {
      state: "delivered",
      label: "Delivered",
      color: "green",
      bgClass: "bg-green-50 text-green-700 border-green-200",
      rowClassName: "",
      date: deliveredByDate,
      tooltip: "Order is delivered",
      daysFromOrder,
      daysRemaining,
    };
  }

  if (today.getTime() > deliveredByDate.getTime()) {
    return {
      state: "delayed",
      label: "Delayed",
      color: "red",
      bgClass: "bg-red-50 text-red-700 border-red-200",
      rowClassName: "delayed-row",
      date: deliveredByDate,
      tooltip: `Order is delayed by ${Math.abs(daysRemaining)} day(s)`,
      daysFromOrder,
      daysRemaining,
    };
  }

  if (daysFromOrder >= 10) {
    return {
      state: "due_soon",
      label: "Due Soon",
      color: "gold",
      bgClass: "bg-yellow-50 text-yellow-700 border-yellow-200",
      rowClassName: "due-soon-row",
      date: deliveredByDate,
      tooltip: `${daysRemaining} day(s) remaining before delay`,
      daysFromOrder,
      daysRemaining,
    };
  }

  return {
    state: "normal",
    label: "On Track",
    color: "blue",
    bgClass: "bg-blue-50 text-blue-700 border-blue-200",
    rowClassName: "",
    date: deliveredByDate,
    tooltip: `${daysRemaining} day(s) remaining`,
    daysFromOrder,
    daysRemaining,
  };
};

const isDelayedOrder = (order) => getDeliveryDeadlineInfo(order).state === "delayed";

const buildOrderSearchText = (order) =>
  [
    `#${order?.id ?? ""}`,
    order?.orderId,
    order?.referenceNumber,
    order?.trackingId,
    formatShortDate(getDeliveredByDate(order)),
    order?.product?.product_name,
    order?.product_name,
    order?.deliveryStatus,
    getCompanyName(order),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const Orders = () => {
  const {
    orders,
    loading,
    pagination,
    setPagination,
    selectedRowKeys,
    setSelectedRows,
    setSelectedRowKeys,
    isSales,
    isOrderCanceller,
    fetchOrders,
    fetchOrderParts,
    updateOrderStatus,
    updateOrderPriority,
    orderParts,
    partsLoading,
    cancelOrderWithReason,
    deleteOrder,
  } = useOrderContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [sortType, setSortType] = useState("");
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [cancelForm] = Form.useForm();
  const [customerFilter, setCustomerFilter] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingCustomerOrders, setLoadingCustomerOrders] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [stockFilter, setStockFilter] = useState(null);
  const [orderTimingTab, setOrderTimingTab] = useState("all");
  const [customOrderModalVisible, setCustomOrderModalVisible] = useState(false);
  const [customOrderForm] = Form.useForm();

  // ── Apart (custom) orders table – separate row selection ──────────────────
  const [selectedApartRowKeys, setSelectedApartRowKeys] = useState([]);
  const [selectedApartRows, setSelectedApartRows] = useState([]);
  // ─────────────────────────────────────────────────────────────────────────

  const location = useLocation();
  const navigate = useNavigate();
  const [incomingScope, setIncomingScope] = useState(null);

  useEffect(() => {
    if (location.state?.ordersScope) {
      setIncomingScope(location.state.ordersScope);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.updatedProduct) {
      fetchOrders();
      navigate("/orders", { replace: true });
    }
  }, [location.state, navigate, fetchOrders]);

  const handleTableChange = (pagination) => setPagination(pagination);

  const handleCancelClick = (rec) => {
    if (rec.status === "cancelled") return;
    if (isSales) {
      message.error("Sales users can only view orders.");
      return;
    }
    if (isOrderCanceller) {
      setOrderToCancel(rec);
      setCancelModalVisible(true);
    } else {
      updateOrderStatus(rec.id, "cancelled");
    }
  };

  const handleUndoCancel = (rec) => {
    confirm({
      title: `Undo cancellation for order #${rec.id}?`,
      icon: <ExclamationCircleOutlined />,
      content: "This will restore the order back to processing.",
      okText: "Yes, Undo",
      cancelText: "No",
      onOk: async () => {
        try {
          await updateOrderStatus(rec.id, "processing");
          message.success("Order restored to processing.");
          if (typeof fetchOrders === "function") fetchOrders();
        } catch (err) {
          console.error("Undo cancel failed:", err);
          message.error("Failed to undo cancellation.");
        }
      },
    });
  };

  const handleCancelOrder = async () => {
    const success = await cancelOrderWithReason(orderToCancel.id, cancelReason);
    if (success) {
      setCancelModalVisible(false);
      setCancelReason("");
      setOrderToCancel(null);
      cancelForm.resetFields();
    }
  };

  const handleDelete = (rec) =>
    confirm({
      title: `Delete order #${rec.id}?`,
      icon: <ExclamationCircleOutlined />,
      content: "This cannot be undone.",
      onOk: async () => {
        const success = await deleteOrder(rec.id);
        if (success) {
          const isLastItemOnPage = orders.length === 1;
          const isNotFirstPage = pagination.current > 1;
          const newCurrent =
            isLastItemOnPage && isNotFirstPage
              ? pagination.current - 1
              : pagination.current;
          setPagination({ ...pagination, current: newCurrent });
        }
      },
    });

  // ── Delete selected – upper (regular) orders ──────────────────────────────
  const handleDeleteSelected = () => {
    if (!selectedRowKeys.length) {
      message.warning("Select at least one order.");
      return;
    }
    confirm({
      title: "Are you sure you want to delete the selected orders?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      onOk: async () => {
        await Promise.all(selectedRowKeys.map((id) => deleteOrder(id)));
        setSelectedRowKeys([]);
      },
    });
  };

  // ── Delete selected – lower (apart / custom) orders ───────────────────────
  const handleDeleteSelectedApart = () => {
    if (!selectedApartRowKeys.length) {
      message.warning("Select at least one apart order.");
      return;
    }
    confirm({
      title: "Are you sure you want to delete the selected apart orders?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      onOk: async () => {
        await Promise.all(selectedApartRowKeys.map((id) => deleteOrder(id)));
        setSelectedApartRowKeys([]);
        setSelectedApartRows([]);
      },
    });
  };

  // ── Send to production – upper (regular) orders ───────────────────────────
  const handleSendToProduction = async () => {
    if (!selectedRowKeys.length) {
      message.warning("Select at least one order.");
      return;
    }
    try {
      await axiosInstance.post("/admin/production/send", {
        orderIds: selectedRowKeys,
      });
      message.success("Selected orders sent to production.");
      setSelectedRowKeys([]);
      fetchOrders();
      navigate("/production-orders");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to send to production.");
    }
  };

  // ── Send to production – lower (apart / custom) orders ────────────────────
  const handleSendToProductionApart = async () => {
    if (!selectedApartRowKeys.length) {
      message.warning("Select at least one apart order.");
      return;
    }
    try {
      await axiosInstance.post("/admin/production/send", {
        orderIds: selectedApartRowKeys,
      });
      message.success("Selected apart orders sent to production.");
      setSelectedApartRowKeys([]);
      setSelectedApartRows([]);
      fetchOrders();
      navigate("/production-orders");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to send apart orders to production.");
    }
  };

  const handleView = (rec) => {
    setSelectedOrder(rec);
    setViewModalVisible(true);
  };

  const closeView = () => {
    setViewModalVisible(false);
    setSelectedOrder(null);
  };

  const getStockStatus = (order) => {
    const stock = order.product?.stock;
    const quantity = order.quantity || 0;
    if (stock === null || stock === undefined)
      return { status: "unknown", text: "Unknown", color: "default" };
    if (stock >= quantity)
      return { status: "in_stock", text: "In Stock", color: "success" };
    if (stock > 0)
      return { status: "low_stock", text: `Low Stock (${stock})`, color: "warning" };
    return { status: "out_of_stock", text: "Out of Stock", color: "error" };
  };

  const customers = useMemo(() => {
    const map = new Map();
    (orders || []).forEach((order) => {
      const id = order.userId ?? order.user ?? order.id;
      if (id == null) return;
      const company_name = String(order.company_name || "").trim();
      const full_name = String(order.full_name || "").trim();
      const delivery_address = String(
        order.shipping_address ||
          order.user?.delivery_address ||
          order.user?.office_address ||
          ""
      ).trim();
      if (!map.has(String(id))) {
        map.set(String(id), { id, company_name, full_name, delivery_address });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.company_name || a.full_name || "").localeCompare(
        b.company_name || b.full_name || ""
      )
    );
  }, [orders]);

  const getCustomerName = (c) =>
    c?.company_name?.trim() || c?.full_name?.trim() || "No Company";

  const getCustomerAddress = (c) => c?.delivery_address || c?.office_address || "";

  const customerOptions = useMemo(() => {
    const counts = new Map();
    (orders || []).forEach((o) => {
      const name = getCompanyName(o) || "-";
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [orders]);

 const getOrderDisplayName = (order) => {
  const name =
    order?.partName ||
    order?.job_name ||
    order?.orderParts?.[0]?.partName ||
    order?.product?.product_name ||
    order?.product_name ||
    order?.special_quote?.customOrder?.productDescription ||
    order?.productDescription ||
    "";
  if (String(name).trim()) return String(name).trim();
  return isCustomerOrderRecord(order) ? "Custom Order" : "No Product";
};
  const getOrderKindTag = (order) => {
    if (isCustomerOrderRecord(order)) {
      return { text: "Custom", color: "gold" };
    }
    return { text: "Normal", color: "green" };
  };

  const categoryOptions = useMemo(() => {
    const counts = new Map();
    (orders || []).forEach((o) => {
      const cat = o.productCategoryDisplay || "Other";
      counts.set(cat, (counts.get(cat) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [orders]);

  const getPreviousOrderDisplayName = (order) => {
    const productName =
      order?.product?.product_name?.trim?.() ||
      order?.product_name?.trim?.() ||
      order?.special_quote?.customOrder?.productDescription?.trim?.() ||
      order?.productDescription?.trim?.() ||
      "";
    if (productName) return productName;
    return isCustomerOrderRecord(order) ? "Custom Order" : "No Product";
  };

  const getPreviousOrderTypeTag = (order) => {
    if (isCustomerOrderRecord(order)) {
      return { text: "Custom", color: "gold" };
    }
    return { text: "Order", color: "green" };
  };

  // ── Apply scope / filters / sort ─────────────────────────────────────────
  let working = orders || [];

  if (incomingScope === "today") {
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
    working = working.filter((o) => {
      const dt = new Date(o.orderDate);
      return !isNaN(dt) && dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === d;
    });
  } else if (incomingScope === "month") {
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth();
    working = working.filter((o) => {
      const dt = new Date(o.orderDate);
      return !isNaN(dt) && dt.getFullYear() === y && dt.getMonth() === m;
    });
  } else if (incomingScope === "awaiting_delivery") {
    working = working.filter((o) =>
      String(o.deliveryStatus || "").toLowerCase().includes("awaiting")
    );
  }

  if (customerFilter)
    working = working.filter((o) => (getCompanyName(o) || "-") === customerFilter);

  if (categoryFilter)
    working = working.filter(
      (o) => (o.productCategoryDisplay || "Other") === categoryFilter
    );

  if (stockFilter === "in")
    working = working.filter((o) => {
      const s = getStockStatus(o).status;
      return s === "in_stock" || s === "low_stock";
    });
  else if (stockFilter === "out")
    working = working.filter((o) => getStockStatus(o).status === "out_of_stock");

  const filtered = working.filter((o) => {
    if (!searchTerm.trim()) return true;
    return buildOrderSearchText(o).includes(searchTerm.trim().toLowerCase());
  });

  const toDate = (d) => {
    if (!d) return 0;
    const t = new Date(d).getTime();
    return isNaN(t) ? 0 : t;
  };

  const sorted = [...filtered];
  if (sortType === "date-asc")
    sorted.sort((a, b) => toDate(a.orderDate) - toDate(b.orderDate));
  else if (sortType === "date-desc")
    sorted.sort((a, b) => toDate(b.orderDate) - toDate(a.orderDate));

  // ── Split: regular orders (upper) vs apart/custom orders (lower) ──────────
  const allRegularOrders = sorted.filter((o) => !isCustomerOrderRecord(o));
  const allApartOrders = sorted.filter((o) => isCustomerOrderRecord(o));

  const delayedRegularOrders = allRegularOrders.filter(isDelayedOrder);
  const delayedApartOrders = allApartOrders.filter(isDelayedOrder);

  const dueSoonRegularOrders = allRegularOrders.filter(
    (o) => getDeliveryDeadlineInfo(o).state === "due_soon"
  );
  const dueSoonApartOrders = allApartOrders.filter(
    (o) => getDeliveryDeadlineInfo(o).state === "due_soon"
  );

  const regularOrders =
    orderTimingTab === "delayed" ? delayedRegularOrders : allRegularOrders;

  const apartOrders =
    orderTimingTab === "delayed" ? delayedApartOrders : allApartOrders;

  const totalDelayedOrders = delayedRegularOrders.length + delayedApartOrders.length;
  const totalDueSoonOrders = dueSoonRegularOrders.length + dueSoonApartOrders.length;
  const totalAllOrders = allRegularOrders.length + allApartOrders.length;
  // ─────────────────────────────────────────────────────────────────────────

  // ── Shared action renderer (works for both tables) ────────────────────────
  const renderActions = (_, rec) => (
    <Space size="small">
      <Tooltip title="View Details">
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleView(rec)}
          className="bg-blue-500 hover:bg-blue-600 border-blue-500"
          size="small"
        />
      </Tooltip>

      {!isSales && (
        <>
          {rec.status === "cancelled" ? (
            <Tooltip title="Undo cancellation">
              <Button
                icon={<CheckCircleOutlined />}
                onClick={() => handleUndoCancel(rec)}
                size="small"
                className="bg-green-100 text-green-700 border border-green-300 hover:bg-green-200"
              />
            </Tooltip>
          ) : (
            <Tooltip title="Cancel order">
              <Button
                danger
                icon={<StopOutlined />}
                onClick={() => handleCancelClick(rec)}
                size="small"
                className="hover:bg-red-50"
              />
            </Tooltip>
          )}
        </>
      )}

      {!(isSales || isOrderCanceller) && (
        <Tooltip title="Delete">
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(rec)}
            size="small"
          />
        </Tooltip>
      )}
    </Space>
  );

  // ── Regular orders columns ────────────────────────────────────────────────
  const regularColumns = [
    {
      title: "Sr.No",
      key: "srno",
      width: 70,
      render: (_, __, index) => (
        <div className="text-center font-medium text-gray-600">
          {(pagination.current - 1) * pagination.pageSize + index + 1}
        </div>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      width: 160,
      render: (_, rec) => {
        const companyName = getCompanyName(rec);
        const getInitials = (name) => {
          if (!name || name === "-") return "C";
          return name
            .split(" ")
            .map((w) => w.charAt(0))
            .join("")
            .toUpperCase()
            .slice(0, 2);
        };
        return (
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
              {getInitials(companyName)}
            </div>
            <div className="text-sm font-medium text-gray-900 leading-tight min-w-0 flex-1">
              {companyName}
            </div>
          </div>
        );
      },
    },
    {
      title: "Order Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 110,
      render: (d) => {
        const date = new Date(d);
        const val = isNaN(date)
          ? String(d || "-")
          : date.toLocaleDateString(undefined, {
              year: "2-digit",
              month: "2-digit",
              day: "2-digit",
            });
        return (
          <div className="text-sm text-gray-600 font-medium bg-gray-50 px-2 py-1 rounded text-center">
            {val}
          </div>
        );
      },
    },
    {
      title: "Size",
      key: "size",
      width: 100,
      render: (_, rec) => {
        const val = rec.size || "-";
        return (
          <div
            className="text-xs font-medium text-gray-800 bg-gray-50 border border-gray-200 px-2 py-1 rounded text-center truncate max-w-[8rem]"
            title={String(val)}
          >
            {String(val)}
          </div>
        );
      },
    },
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
      width: 160,
      render: (text, rec) => {
        const displayName = getOrderDisplayName(rec);
        const kindTag = getOrderKindTag(rec);
        return (
          <div className="flex items-center gap-2 truncate max-w-[16rem]" title={displayName}>
            <span className="truncate">{splitProductName(displayName)}</span>
            <Tag color={kindTag.color} className="m-0 text-[11px] px-2 py-0.5 rounded-full">
              {kindTag.text}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "Fabric / Colour",
      key: "fabric_colour",
      width: 160,
      render: (_, rec) => {
        const fabric = rec.fabric_name || "-";
        const color = rec.fabric_color_name || "-";
        return (
          <div className="flex flex-col text-center leading-tight">
            <span className="text-xs font-semibold text-gray-900 truncate" title={fabric}>
              {fabric}
            </span>
            <span className="text-[11px] text-gray-500 truncate" title={color}>
              {color}
            </span>
          </div>
        );
      },
    },
    {
      title: "Customer Ref",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      width: 150,
      render: (val) => {
        const text = String(val || "-");
        return (
          <div
            className="font-mono text-sm bg-gray-50 px-2 py-1 rounded border border-gray-200 whitespace-nowrap"
            title={text}
          >
            <span className="block max-w-[10ch] truncate">{text}</span>
          </div>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      render: (val) => (
        <div className="text-center font-bold text-gray-700">{val}</div>
      ),
    },
    {
      title: "Stock",
      key: "stock_status",
      width: 130,
      render: (_, rec) => {
        const stockInfo = getStockStatus(rec);
        const stockQuantity = rec.product?.stock ?? "Unknown";
        return (
          <Tooltip title={`Available Stock: ${stockQuantity}`}>
            <Tag
              icon={
                stockInfo.status === "in_stock" ? (
                  <CheckCircleOutlined />
                ) : stockInfo.status === "low_stock" ? (
                  <ExclamationCircleOutlinedIcon />
                ) : stockInfo.status === "out_of_stock" ? (
                  <CloseCircleOutlined />
                ) : null
              }
              color={
                stockInfo.status === "in_stock"
                  ? "success"
                  : stockInfo.status === "low_stock"
                  ? "warning"
                  : stockInfo.status === "out_of_stock"
                  ? "error"
                  : "default"
              }
              className="text-xs font-medium"
            >
              {stockInfo.status === "low_stock"
                ? `${stockInfo.text} (${stockQuantity})`
                : stockInfo.text}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Priority",
      key: "priority",
      width: 100,
      render: (_, rec) =>
        isSales || isOrderCanceller ? (
          <span
            className={`px-2 py-1 rounded-full text-xs font-bold ${
              rec.priority === "urgent"
                ? "bg-red-100 text-red-800 border border-red-200"
                : "bg-green-100 text-green-800 border border-green-200"
            }`}
          >
            {rec.priority}
          </span>
        ) : (
          <Select
            value={rec.priority}
            style={{ width: 80 }}
            onChange={(v) => updateOrderPriority(rec.id, v)}
            className="priority-select"
          >
            <Option value="normal">Normal</Option>
            <Option value="urgent">Urgent</Option>
          </Select>
        ),
    },
    {
      title: "Schedule Date",
      dataIndex: "scheduleDate",
      key: "scheduleDate",
      width: 90,
      render: (d) => {
        const date = new Date(d);
        const text = d ? (isNaN(date) ? String(d) : date.toLocaleDateString()) : "-";
        return (
          <div className="text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded text-center font-medium">
            {text}
          </div>
        );
      },
    },
    {
      title: "Delivery Status",
      key: "delivery",
      width: 160,
      render: (_, rec) => {
        const raw = String(rec.deliveryStatus || "").toLowerCase();
        let config = {
          color: "default",
          icon: null,
          text: rec.deliveryStatus || "Not set",
          tooltip: rec.deliveryStatus || "No delivery status set",
        };
        if (raw.includes("awaiting")) {
          config = {
            color: "orange",
            icon: <ExclamationCircleOutlinedIcon />,
            text: rec.deliveryStatus,
            tooltip: "Order is awaiting dispatch",
          };
        } else if (raw.includes("scheduled")) {
          config = {
            color: "purple",
            icon: <ClockCircleOutlined />,
            text: rec.deliveryStatus,
            tooltip: "Delivery has been scheduled",
          };
        } else if (raw.includes("delivered")) {
          config = {
            color: "green",
            icon: <CheckCircleOutlined />,
            text: rec.deliveryStatus,
            tooltip: "Order delivered to customer",
          };
        } else if (raw.includes("cancel")) {
          config = {
            color: "red",
            icon: <CloseCircleOutlined />,
            text: rec.deliveryStatus,
            tooltip: "Delivery has been cancelled",
          };
        }
        return (
          <Tooltip title={config.tooltip}>
            <Tag
              icon={config.icon}
              color={config.color}
              className="text-xs font-medium px-3 py-1 rounded-full"
            >
              {config.text}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Delivered By",
      key: "deliveredBy",
      width: 140,
      render: (_, rec) => {
        const info = getDeliveryDeadlineInfo(rec);
        const dateText = info.date ? formatShortDate(info.date) : "-";

        return (
          <Tooltip title={info.tooltip}>
            <div
              className={`text-xs font-semibold px-2 py-1 rounded border text-center ${info.bgClass}`}
            >
              <div>{dateText}</div>
              <Tag color={info.color} className="m-0 mt-1 text-[10px] leading-4">
                {info.label}
              </Tag>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 190,
      fixed: "right",
      render: renderActions,
    },
  ];

  // ── Apart Orders columns (Order-Parts style) ──────────────────────────────
  const apartColumns = [
    {
      title: "Sr.No",
      key: "srno",
      width: 60,
      render: (_, __, index) => (
        <div className="text-center font-medium text-gray-600">{index + 1}</div>
      ),
    },
    {
      title: "Order ID",
      key: "orderId",
      width: 90,
      render: (_, rec) => (
        <span className="font-mono text-sm font-semibold text-gray-800">
          {rec.id ?? rec.orderId ?? "-"}
        </span>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      width: 150,
      render: (_, rec) => (
        <span className="text-sm font-medium text-gray-900">
          {getCompanyName(rec)}
        </span>
      ),
    },
    {
      title: "Delivered By",
      key: "deliveredBy",
      width: 135,
      render: (_, rec) => {
        const info = getDeliveryDeadlineInfo(rec);
        const dateText = info.date ? formatShortDate(info.date) : "-";

        return (
          <Tooltip title={info.tooltip}>
            <div
              className={`text-xs font-semibold px-2 py-1 rounded border text-center ${info.bgClass}`}
            >
              <div>{dateText}</div>
              <Tag color={info.color} className="m-0 mt-1 text-[10px] leading-4">
                {info.label}
              </Tag>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Type",
      key: "type",
      width: 150,
      render: (_, rec) => {
        // Determine if it's a replacement (£0) or chargeable based on price fields
        const salePrice =
          Number(rec.saleUnitPrice ?? rec.sale_unit_price ?? rec.price ?? 0);
        const isReplacement = salePrice === 0;
        return isReplacement ? (
          <Tag color="blue" className="text-xs font-medium">
            Replacement (£0)
          </Tag>
        ) : (
          <Tag color="orange" className="text-xs font-medium">
            Chargeable
          </Tag>
        );
      },
    },
   {
  title: "Part Name",
  key: "partName",
  render: (_, rec) => {
    const name = rec.partName || rec.job_name || getOrderDisplayName(rec);
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-800 font-medium" title={name}>
          {name}
        </span>
        <Tag color="purple" className="m-0">Order from Parts</Tag>
      </div>
    );
  },
},
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 60,
      render: (val) => (
        <div className="text-center font-bold text-gray-700">{val ?? "-"}</div>
      ),
    },
    {
      title: "Sale Price",
      key: "salePrice",
      width: 110,
      render: (_, rec) => {
        const val = Number(
          rec.saleUnitPrice ?? rec.sale_unit_price ?? rec.price ?? 0
        );
        return (
          <span className="text-sm font-semibold text-gray-800">
            £{val.toFixed(2)}
          </span>
        );
      },
    },
    {
      title: "Cost Price",
      key: "costPrice",
      width: 110,
      render: (_, rec) => {
        const val = Number(
          rec.costUnitPrice ?? rec.cost_unit_price ?? rec.costPrice ?? 0
        );
        return (
          <span className="text-sm font-semibold text-gray-800">
            £{val.toFixed(2)}
          </span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, rec) => {
        const status = rec.partStatus || rec.status || "pending";
        return <Tag className="text-xs">{status}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      fixed: "right",
      render: renderActions,
    },
  ];
  // ─────────────────────────────────────────────────────────────────────────

  // ── View modal detail renderer ─────────────────────────────────────────────
  const renderViewModal = () =>
    selectedOrder && (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">Customer</div>
            <div className="font-semibold text-gray-900">
              {getCompanyName(selectedOrder)}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">Product Name</div>
            <div className="font-semibold text-gray-900">
              {selectedOrder.product_name}
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600">Customer Name</div>
          <div className="font-semibold text-gray-900">
            {selectedOrder.customer?.company_name ||
              selectedOrder.customer?.full_name ||
              selectedOrder.customer?.email ||
              getCompanyName(selectedOrder)}
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600">Customer Address</div>
          <div className="font-semibold text-gray-900">
            {selectedOrder.customer?.shipping_address ||
              selectedOrder.shipping_address ||
              "-"}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">Order Date</div>
            <div className="font-semibold text-gray-900">
              {formatShortDate(getOrderBaseDate(selectedOrder))}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">Deliver By</div>
            <div className="font-semibold text-gray-900">
              {formatShortDate(getDeliveredByDate(selectedOrder))}
              <Tag
                color={getDeliveryDeadlineInfo(selectedOrder).color}
                className="ml-2"
              >
                {getDeliveryDeadlineInfo(selectedOrder).label}
              </Tag>
            </div>
          </div>
        </div>

        {selectedOrder.product_image && (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-2">Product Image</div>
            <Image
              src={selectedOrder.product_image}
              width={200}
              height={140}
              style={{ objectFit: "cover", borderRadius: 8 }}
              preview={{ src: selectedOrder.product_image }}
              className="border border-gray-200"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <div className="text-sm text-gray-600">Bed Size</div>
              <div className="font-semibold text-gray-900">
                {selectedOrder.size || "—"}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Fabric Type</div>
              <div className="font-semibold text-gray-900">
                {selectedOrder.fabric_name || "—"}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Fabric Colour</div>
              <div className="font-semibold text-gray-900">
                {selectedOrder.fabric_color_name || "—"}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Storage Type</div>
              <div className="font-semibold text-gray-900">
                {selectedOrder.storageType || "Non Storage"}
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-gray-600">Quantity</div>
              <div className="font-semibold text-gray-900">{selectedOrder.quantity}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Customer Reference</div>
              <div className="font-semibold text-gray-900">
                {selectedOrder.referenceNumber || "-"}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Priority</div>
              <span
                className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                  selectedOrder.priority === "urgent"
                    ? "bg-red-100 text-red-800"
                    : "bg-green-100 text-green-800"
                }`}
              >
                {selectedOrder.priority}
              </span>
            </div>
            <div>
              <div className="text-sm text-gray-600">Order Status</div>
              <span
                className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                  selectedOrder.status === "cancelled"
                    ? "bg-red-100 text-red-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {selectedOrder.status}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-2">Stock Status</div>
          <div className="font-medium">
            {(() => {
              const stockInfo = getStockStatus(selectedOrder);
              return (
                <Tag
                  icon={
                    stockInfo.status === "in_stock" ? (
                      <CheckCircleOutlined />
                    ) : stockInfo.status === "low_stock" ? (
                      <ExclamationCircleOutlinedIcon />
                    ) : stockInfo.status === "out_of_stock" ? (
                      <CloseCircleOutlined />
                    ) : null
                  }
                  color={
                    stockInfo.status === "in_stock"
                      ? "success"
                      : stockInfo.status === "low_stock"
                      ? "warning"
                      : stockInfo.status === "out_of_stock"
                      ? "error"
                      : "default"
                  }
                >
                  {stockInfo.text}{" "}
                  {stockInfo.status === "low_stock" &&
                    `(Available: ${selectedOrder.product?.stock || 0})`}
                </Tag>
              );
            })()}
          </div>
        </div>

        {(() => {
          const special = normalizeSpecialQuote(selectedOrder);
          if (!special.hasAny) return null;
          const pdfs = special.files.filter((f) => f.isPdf);
          const nonPdfs = special.files.filter((f) => !f.isPdf);
          return (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-2">Special Instructions</div>
              {String(special.text || "").trim() && (
                <div className="font-medium text-gray-900 whitespace-pre-wrap">
                  {special.text}
                </div>
              )}
              {special.files.length > 0 && (
                <div className="mt-3 space-y-3">
                  {nonPdfs.length > 0 && (
                    <div className="space-y-2">
                      {nonPdfs.map((f, idx) => (
                        <div key={`${f.url}-${idx}`} className="flex items-center gap-3">
                          {f.isImage ? (
                            <Image
                              src={f.url}
                              width={80}
                              height={60}
                              style={{ objectFit: "cover", borderRadius: 8 }}
                              preview={{ src: f.url }}
                              className="border border-gray-200"
                            />
                          ) : (
                            <div className="text-sm text-gray-700">
                              <a
                                href={f.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 underline"
                              >
                                {f.name || "Open file"}
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {pdfs.length > 0 && (
                    <div className="space-y-2">
                      {pdfs.map((f, idx) => (
                        <div key={`${f.url}-${idx}`} className="text-sm">
                          <a
                            href={f.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 underline"
                          >
                            {f.name || "Open PDF"}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {String(selectedOrder.status || "").toLowerCase() === "cancelled" && (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-2">Cancellation Reason</div>
            <div className="font-medium text-gray-900">
              {selectedOrder.cancelReason ||
                selectedOrder.cancel_reason ||
                selectedOrder.cancellationReason ||
                "No cancellation reason provided"}
            </div>
          </div>
        )}
      </div>
    );

  return (
    <Spin spinning={loading} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">

          {/* ── Page header ─────────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Orders Management</h1>
                <p className="text-gray-600">Manage and track all customer orders</p>
              </div>
              {!(isSales || isOrderCanceller) && (
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="primary"
                    onClick={handleSendToProduction}
                    className="bg-green-500 hover:bg-green-600 border-green-500 h-10 px-4"
                  >
                    Send to Production
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => setCustomOrderModalVisible(true)}
                    className="bg-purple-500 hover:bg-purple-600 border-purple-500 h-10 px-4"
                  >
                    Customer Order
                  </Button>
                  <Button
                    type="primary"
                    danger
                    onClick={handleDeleteSelected}
                    className="h-10 px-4"
                  >
                    Delete Selected
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* ── Filters ────────────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <SearchOutlined className="mr-2" /> Search Orders
                </label>
                <Input
                  placeholder="Search by order ID, reference no, customer, product..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="w-full h-10 rounded-lg"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" /> Sort By
                </label>
                <Select
                  placeholder="Sort orders..."
                  value={sortType || undefined}
                  onChange={setSortType}
                  allowClear
                  className="w-full h-10 rounded-lg"
                >
                  <Option value="date-asc">Date ↑ (Oldest First)</Option>
                  <Option value="date-desc">Date ↓ (Newest First)</Option>
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <UserOutlined className="mr-2" /> Filter by Customer
                </label>
                <Select
                  placeholder="Select customer..."
                  value={customerFilter || undefined}
                  onChange={setCustomerFilter}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  className="w-full h-10 rounded-lg"
                >
                  {customerOptions.map(({ name, count }) => (
                    <Option key={name} value={name} label={name}>
                      <div className="flex justify-between items-center">
                        <span>{name}</span>
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                          {count}
                        </span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" /> Category
                </label>
                <Select
                  placeholder="Select category..."
                  value={categoryFilter || undefined}
                  onChange={setCategoryFilter}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  className="w-full h-10 rounded-lg"
                >
                  {categoryOptions.map(({ name, count }) => (
                    <Option key={name} value={name} label={name}>
                      <div className="flex justify-between items-center">
                        <span>{name}</span>
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                          {count}
                        </span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" /> Stock Filter
                </label>
                <Select
                  placeholder="All stock statuses"
                  value={stockFilter || undefined}
                  onChange={setStockFilter}
                  allowClear
                  className="w-full h-10 rounded-lg"
                >
                  <Option value="in">In Stock (incl. Low)</Option>
                  <Option value="out">Out of Stock</Option>
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2" /> Order Summary
                </label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 h-10 flex items-center">
                  <div className="text-sm font-medium text-gray-900">
                    Total: {regularOrders.length + apartOrders.length} orders
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              UPPER TABLE – Regular (non-custom) orders ONLY
          ══════════════════════════════════════════════════════════════ */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <h2 className="text-base font-semibold text-gray-800">Orders</h2>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                  {regularOrders.length}
                </span>
                {totalDueSoonOrders > 0 && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                    Due Soon: {totalDueSoonOrders}
                  </span>
                )}
                {totalDelayedOrders > 0 && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                    Delayed: {totalDelayedOrders}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="small"
                  type={orderTimingTab === "all" ? "primary" : "default"}
                  onClick={() => setOrderTimingTab("all")}
                >
                  All Orders ({totalAllOrders})
                </Button>
                <Button
                  size="small"
                  danger={orderTimingTab === "delayed"}
                  type={orderTimingTab === "delayed" ? "primary" : "default"}
                  onClick={() => setOrderTimingTab("delayed")}
                >
                  Delayed Orders ({totalDelayedOrders})
                </Button>
              </div>
            </div>
            <Table
  dataSource={regularOrders}
  columns={regularColumns}
  rowKey="id"
  rowSelection={
    !(isSales || isOrderCanceller)
      ? {
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }
      : undefined
  }
  pagination={{
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: regularOrders.length,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} items`,
    className: "px-6 py-4",
    pageSizeOptions: ["10", "20", "50", "100"],
  }}
  onChange={handleTableChange}
  scroll={{ x: "max-content" }}
  className="modern-orders-table"
  rowClassName={(record) => {
    const status = String(record.status || "").toLowerCase();

    if (status === "cancelled") return "cancelled-row";

    return getDeliveryDeadlineInfo(record).rowClassName;
  }}
/>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              LOWER TABLE – Apart / Custom orders
              Uses Order-Parts style columns:
              Sr.No | Order ID | Customer | Type | Part Name | Qty | Sale Price | Cost Price | Status | Actions
          ══════════════════════════════════════════════════════════════ */}
          <div className="bg-white rounded-2xl shadow-sm border border-purple-200 overflow-hidden mb-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-purple-200 bg-purple-50">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-purple-500" />
                <h2 className="text-base font-semibold text-purple-800">Parts Order</h2>
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                  {apartOrders.length}
                </span>
              </div>

              {/* Apart-orders action buttons */}
              {!(isSales || isOrderCanceller) && (
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="primary"
                    onClick={handleSendToProductionApart}
                    className="bg-green-500 hover:bg-green-600 border-green-500 h-9 px-4"
                  >
                    Send to Production
                  </Button>
                  <Button
                    type="primary"
                    danger
                    onClick={handleDeleteSelectedApart}
                    className="h-9 px-4"
                  >
                    Delete Selected
                  </Button>
                </div>
              )}
            </div>

            <Table
  dataSource={apartOrders}
  columns={apartColumns}
  rowKey="id"
  rowSelection={
    !(isSales || isOrderCanceller)
      ? {
          selectedRowKeys: selectedApartRowKeys,
          onChange: (keys, rows) => {
            setSelectedApartRowKeys(keys);
            setSelectedApartRows(rows);
          },
        }
      : undefined
  }
  pagination={{
    pageSize: 10,
    total: apartOrders.length,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} items`,
    className: "px-6 py-4",
    pageSizeOptions: ["10", "20", "50", "100"],
  }}
  scroll={{ x: "max-content" }}
  className="modern-orders-table"
  locale={{
    emptyText: (
      <div className="py-8 text-center text-gray-400 text-sm">
        No apart (custom) orders found
      </div>
    ),
  }}
  rowClassName={(record) => {
    const status = String(record.status || "").toLowerCase();

    if (status === "cancelled") return "cancelled-row";

    return getDeliveryDeadlineInfo(record).rowClassName;
  }}
/>
          </div>

        </div>
      </div>

      {/* ── Cancel modal ─────────────────────────────────────────────────── */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <StopOutlined className="text-red-500" />
            <span>Cancel Order #{orderToCancel?.id || ""}</span>
          </div>
        }
        open={cancelModalVisible}
        onOk={handleCancelOrder}
        onCancel={() => {
          setCancelModalVisible(false);
          setCancelReason("");
          setOrderToCancel(null);
          cancelForm.resetFields();
        }}
        okText="Confirm Cancellation"
        cancelText="Keep Order"
        okButtonProps={{ danger: true }}
        className="modern-modal"
      >
        <Form form={cancelForm} layout="vertical">
          <Form.Item
            label="Reason for Cancellation"
            name="cancelReason"
            rules={[{ required: true, message: "Please provide a cancellation reason" }]}
          >
            <TextArea
              rows={4}
              placeholder="Enter the reason for cancelling this order..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="rounded-lg border-gray-300"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── View details modal ───────────────────────────────────────────── */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <EyeOutlined className="text-blue-500" />
            <span>Order Details #{selectedOrder?.id || ""}</span>
          </div>
        }
        open={viewModalVisible}
        onCancel={closeView}
        footer={[
          <Button key="close" onClick={closeView} className="rounded-lg">
            Close
          </Button>,
        ]}
        width={1000}
        className="modern-modal"
      >
        {renderViewModal()}
      </Modal>

      {/* ── Create Customer Order modal ──────────────────────────────────── */}
      <Modal
        title="Create Customer Order"
        open={customOrderModalVisible}
        onCancel={() => {
          setCustomOrderModalVisible(false);
          customOrderForm.resetFields();
          setCustomerOrders([]);
          setLoadingCustomerOrders(false);
        }}
        onOk={() => customOrderForm.submit()}
        okText="Create Order"
        cancelText="Cancel"
        width={720}
      >
        <Form
          form={customOrderForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              const payload = {
                userId: values.userId,
                shipping_address: values.shipping_address,
                referenceNumber: values.referenceNumber,
                productDescription: values.productDescription,
                sizeMaterial: values.sizeMaterial,
                quantity: values.quantity,
                priceMode: values.priceMode,
                saleUnitPrice:
                  values.priceMode === "replacement" ? 0 : Number(values.price || 0),
                costUnitPrice: 0,
                notes: values.notes,
                urgent: values.urgent,
                existingOrderId: values.existingOrderId,
              };

              await axiosInstance.post("/order/create-custom-order", payload);
              message.success("Customer order created successfully");
              setCustomOrderModalVisible(false);
              customOrderForm.resetFields();
              setCustomerOrders([]);
              fetchOrders();
            } catch (e) {
              message.error(
                e?.response?.data?.message || "Failed to create customer order"
              );
            }
          }}
        >
          <Form.Item
            name="userId"
            label="Select Customer"
            rules={[{ required: true, message: "Please select a customer" }]}
          >
            <Select
              showSearch
              placeholder="Search customer by company name"
              optionFilterProp="label"
              optionLabelProp="label"
              filterOption={(input, option) =>
                String(option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
              className="w-full"
              onChange={async (value) => {
                const customer = customers.find((c) => String(c.id) === String(value));
                customOrderForm.setFieldsValue({
                  shipping_address: getCustomerAddress(customer),
                  existingOrderId: undefined,
                  productDescription: "",
                  sizeMaterial: "",
                  quantity: 1,
                });
                setCustomerOrders([]);
                setLoadingCustomerOrders(true);
                try {
                  const { data } = await axiosInstance.get("/order/user-orders", {
                    params: { userId: value },
                  });
                  setCustomerOrders(Array.isArray(data?.orders) ? data.orders : []);
                } catch (err) {
                  console.error("Failed to load customer orders:", err);
                  message.error("Failed to load customer orders");
                } finally {
                  setLoadingCustomerOrders(false);
                }
              }}
            >
              {customers.map((c) => {
                const companyName = getCustomerName(c);
                return (
                  <Option key={c.id} value={c.id} label={companyName}>
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{companyName}</span>
                    </div>
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="existingOrderId"
            label="Search & Select Previous Order (Optional)"
          >
            <Select
              showSearch
              allowClear
              placeholder={
                loadingCustomerOrders
                  ? "Loading orders..."
                  : customerOrders.length === 0
                  ? "Select a customer first to see their orders"
                  : `Search from ${customerOrders.length} order(s)…`
              }
              loading={loadingCustomerOrders}
              optionFilterProp="label"
              optionLabelProp="title"
              filterOption={(input, option) =>
                String(option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
              notFoundContent={
                loadingCustomerOrders ? (
                  <Spin size="small" />
                ) : customerOrders.length === 0 ? (
                  <div className="text-gray-400 text-sm py-2 text-center">
                    No orders found. Select a customer first.
                  </div>
                ) : (
                  "No matching orders"
                )
              }
              onChange={(orderId) => {
                const order = customerOrders.find(
                  (o) => String(o.id) === String(orderId)
                );
                if (!order) return;
                const displayName = getPreviousOrderDisplayName(order);
                customOrderForm.setFieldsValue({
                  productDescription: displayName,
                  sizeMaterial: `${order.size?.size || order.size || ""} ${
                    order.fabric?.fabric_name || order.fabric_name || ""
                  }`.trim(),
                  quantity: order.quantity || 1,
                });
              }}
            >
              {customerOrders.map((o) => {
                const displayName = getPreviousOrderDisplayName(o);
                const typeTag = getPreviousOrderTypeTag(o);
                const searchLabel = [
                  `#${o.id}`,
                  displayName,
                  o.orderId,
                  o.referenceNumber,
                  o.trackingId,
                  o.orderDate,
                  o.size?.size || o.size,
                  o.fabric?.fabric_name || o.fabric_name,
                  isCustomerOrderRecord(o) ? "custom" : "order",
                ]
                  .filter(Boolean)
                  .join(" ")
                  .toLowerCase();

                return (
                  <Option
                    key={o.id}
                    value={o.id}
                    title={displayName}
                    label={searchLabel}
                  >
                    <div className="flex flex-col py-1">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-medium text-gray-900 text-sm truncate">
                          {displayName}
                        </span>
                        <div className="flex items-center gap-2 shrink-0">
                          <Tag
                            color={typeTag.color}
                            className="m-0 text-[11px] px-2 py-0.5 rounded-full"
                          >
                            {typeTag.text}
                          </Tag>
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 font-mono">
                            #{o.id}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Ref: {o.referenceNumber || "—"} · {o.orderDate || "—"}
                        {o.orderId ? ` · ${o.orderId}` : ""}
                      </div>
                    </div>
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="productDescription"
            label="Product Description"
            rules={[{ required: true, message: "Please enter product description" }]}
          >
            <Input placeholder="Product description" />
          </Form.Item>

          <Form.Item
            name="sizeMaterial"
            label="Size & Material"
            rules={[{ required: true, message: "Please enter size & material" }]}
          >
            <Input placeholder="Size and material" />
          </Form.Item>

          <Form.Item
            name="quantity"
            label="QTY"
            initialValue={1}
            rules={[{ required: true, message: "Please enter quantity" }]}
          >
            <Input type="number" min={1} />
          </Form.Item>

         <Form.Item
  name="priceMode"
  label="Price / Replacement"
  initialValue="replacement"
  rules={[{ required: true }]}
  required={false}
>
            <Select>
              <Option value="replacement">Replacement (£0)</Option>
              <Option value="paid">Chargeable</Option>
            </Select>
          </Form.Item>

          {/* ── Price field shown only when "Chargeable" is selected ── */}
         <Form.Item
  noStyle
  shouldUpdate={(prev, curr) => prev.priceMode !== curr.priceMode}
>
  {({ getFieldValue }) =>
    getFieldValue("priceMode") === "paid" ? (
      <Form.Item name="price" label="Price (£)" required={false}>
        <Input type="number" min={0} step="0.01" placeholder="0.00" />
      </Form.Item>
    ) : null
  }
</Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item name="urgent" valuePropName="checked">
            <Checkbox>Urgent</Checkbox>
          </Form.Item>

          <Form.Item
            name="shipping_address"
            label="Shipping Address"
            rules={[{ required: true, message: "Please enter shipping address" }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item name="referenceNumber" label="Reference Number">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <style jsx>{`
        .modern-orders-table .ant-table-thead > tr > th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
          padding: 12px 16px;
        }
        .modern-orders-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 12px 16px;
          transition: all 0.2s ease;
        }
        .modern-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }
        .modern-modal .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
        .priority-select .ant-select-selector {
          border-radius: 8px !important;
        }
          .modern-orders-table .ant-table-tbody > tr.due-soon-row > td {
  background: #fffbeb !important;
}

.modern-orders-table .ant-table-tbody > tr.due-soon-row:hover > td {
  background: #fef3c7 !important;
}

.modern-orders-table .ant-table-tbody > tr.due-soon-row > td:first-child {
  border-left: 4px solid #f59e0b;
}

.modern-orders-table .ant-table-tbody > tr.delayed-row > td {
  background: #fff1f2 !important;
}

.modern-orders-table .ant-table-tbody > tr.delayed-row:hover > td {
  background: #ffe4e6 !important;
}

.modern-orders-table .ant-table-tbody > tr.delayed-row > td:first-child {
  border-left: 4px solid #dc2626;
}

          .modern-orders-table .ant-table-tbody > tr.cancelled-row > td {
  background: #fecaca !important;
}

.modern-orders-table .ant-table-tbody > tr.cancelled-row:hover > td {
  background: #fca5a5 !important;
}

.modern-orders-table .ant-table-tbody > tr.cancelled-row > td:first-child {
  border-left: 4px solid #dc2626;
}
      `}</style>
    </Spin>
  );
};

export default Orders;
